///
/// EarthBrowser
///
/// Created by Matt Giger on 9/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type Earth } from "@/earthbrowser/activities/EarthActivity/Earth"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { ImergOverlay } from "@/earthbrowser/dataset-distribution/imerg/ImergOverlay"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { isMobile } from "@/earthbrowser/utils/platform"
import { time, timedelta } from "@stovokor/math/time"
import { DatasetLayer, type ArrowRow, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { ImergTable, type ImergFrameRecord } from "./ImergTable"

// Frames are 30-minute slots. Tier frame limits per the Phase 4 depth gates
// (SUBSCRIPTION_PRICING_MODEL.md): free 2 hours everywhere, paid 48 hours on
// desktop and 12 on mobile (the pre-existing device cap, kept to limit sync
// and image-cache churn). Texture memory stays bounded regardless
// (RadarTextureSeries purges frames outside the preload window).
const cPaidDesktopFrameLimit = 96
const cPaidMobileFrameLimit = 24
const cFreeFrameLimit = 4

/** Effective frame limit: min(tier limit, device cap) */
function frameLimit(): number {
	const deviceCap = isMobile() ? cPaidMobileFrameLimit : cPaidDesktopFrameLimit
	// No store (embed builds) means free-tier caps
	return isPaidEntitlement() ? deviceCap : cFreeFrameLimit
}

/** Shape of an imerg Arrow row (only fields we read) */
type ImergRow = {
	id: string
	product: string
	data_timestamp: string
	slot_timestamp: string
	image_url: string
	source_path: string
	size_bytes: number
	width: number
	height: number
	vmin: number
	vmax: number
	bounds_wgs84: string | null | undefined
	created_at: string
}

/**
 * Global precipitation layer from NASA GPM IMERG Early Run. Shares the
 * "Live Precipitation" visibility toggle with the MRMS layer so the globe shows
 * satellite-estimated precipitation everywhere and sharper ground radar
 * over CONUS.
 */
export class ImergDatasetLayer extends DatasetLayer<ImergFrameRecord> {
	private imergOverlay: ImergOverlay
	private earth: Earth
	// Cache frames by URL to avoid reloading unchanged frames
	private frameCache = new Map<string, RadarTextureFrame>()
	// Redux state subscription unsubscribe function
	private unsubscribe: (() => void) | null = null
	// Last seen entitlement tier, to re-query frames when depth changes
	// (e.g. checkout completes and the 48-hour window unlocks live)
	private lastPaidState = false

	constructor(context: EarthBrowserContext, earth: Earth) {
		super(context, "imerg")
		this.imergOverlay = new ImergOverlay(context)
		this.earth = earth

		// Initialize visibility based on current state
		const initialVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("precipitation") ?? false
		this.visible = initialVisible
		this.lastPaidState = isPaidEntitlement()
		if (initialVisible) {
			this.earth.addOverlay(this.imergOverlay)
		}

		// Subscribe to visibility and entitlement state changes
		this.unsubscribe = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			if (!state) return
			this.setVisible(state.earthbrowser.visibleDatasets.includes("precipitation"))
			if (state.entitlements.isPaid !== this.lastPaidState) {
				this.lastPaidState = state.entitlements.isPaid
				void this.refresh()
			}
		})
	}

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "imerg",
			// New frames appear roughly every 30 minutes (IMERG Early latency ~4h)
			pollIntervalMs: 300_000,
			needsSync: true,
			supportsDelta: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await ImergTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return
		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)
		const frames = this.parseRows(rows)
		await ImergTable.replaceAll(frames)
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		const upserts = this.parseRows(upsertRows)
		if (upserts.length > 0) {
			await ImergTable.upsert(upserts)
		}
	}

	private parseRows(arrowRows: ArrowRow[]): ImergFrameRecord[] {
		const rows = arrowRows as ImergRow[]
		return rows.map(r => ({
			id: String(r.id),
			product: String(r.product),
			data_timestamp: String(r.data_timestamp),
			slot_timestamp: String(r.slot_timestamp),
			image_url: String(r.image_url),
			source_path: String(r.source_path),
			size_bytes: Number(r.size_bytes),
			width: Number(r.width),
			height: Number(r.height),
			vmin: Number(r.vmin),
			vmax: Number(r.vmax),
			bounds_wgs84: r.bounds_wgs84 === null || r.bounds_wgs84 === undefined ? null : String(r.bounds_wgs84),
			created_at: String(r.created_at),
		}))
	}

	public async queryFeatures(): Promise<ImergFrameRecord[]> {
		const rows = await ImergTable.getRecent(frameLimit())
		return rows
	}

	public createSceneObject(_feature: ImergFrameRecord): DatasetSceneObject<ImergFrameRecord> | undefined {
		// IMERG frames are consumed by overlay rendering, not by creating scene objects.
		return undefined
	}

	public async onSyncComplete(): Promise<void> {
		await this.refresh()
	}

	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		const range = this.imergOverlay.getTimeRange()
		return range ? { startMs: range.start.date.getTime(), endMs: range.end.date.getTime() } : null
	}

	public override getTimeLaneDetail(): TimeLaneDetail | null {
		return this.imergOverlay.getTimeLaneDetail()
	}

	protected override frameLoadState(): LayerLoadState | null {
		return this.imergOverlay.getLoadState()
	}

	/**
	 * Refresh IMERG frames from the database and update the overlay.
	 * Uses delta calculation to reuse existing frames and only load new ones.
	 */
	public override async refresh(): Promise<void> {
		try {
			const records = await ImergTable.getRecent(frameLimit())
			const textureFrames = this.updateFrameCache(records)
			this.imergOverlay.setFrames(textureFrames)
		} catch (error) {
			console.error("[ImergDatasetLayer] Failed to refresh:", error)
		}
	}

	/**
	 * Update the frame cache with new records.
	 * - Reuses existing frames for URLs that are still valid
	 * - Creates new frames for new URLs
	 * - Purges frames for URLs no longer in the record set
	 */
	private updateFrameCache(records: ImergFrameRecord[]): RadarTextureFrame[] {
		// Records are sorted desc by slot_timestamp, we want chronological order
		const sortedRecords = [...records].reverse()

		// Build set of URLs from current records
		const currentUrls = new Set(sortedRecords.map(r => r.image_url))

		// Find and purge frames that are no longer needed
		const urlsToRemove: string[] = []
		for (const [url, frame] of this.frameCache) {
			if (!currentUrls.has(url)) {
				frame.purge()
				urlsToRemove.push(url)
			}
		}
		for (const url of urlsToRemove) {
			this.frameCache.delete(url)
		}

		// Build the frames array, reusing cached frames or creating new ones
		const frames: RadarTextureFrame[] = sortedRecords.map(record => {
			const existingFrame = this.frameCache.get(record.image_url)
			if (existingFrame) {
				return existingFrame
			}

			const startTime = new time(record.slot_timestamp)
			const duration = timedelta.minutes(30)
			// wrapLongitude: frames are global, and flow-advected samples must
			// wrap at the antimeridian instead of clamping
			const newFrame = new RadarTextureFrame(this.context, record.image_url, startTime, duration, true)
			this.frameCache.set(record.image_url, newFrame)
			return newFrame
		})

		return frames
	}

	/**
	 * Set visibility of the IMERG layer
	 */
	public setVisible(visible: boolean): void {
		if (this.visible === visible) return

		this.visible = visible

		if (visible) {
			this.earth.addOverlay(this.imergOverlay)
		} else {
			this.earth.removeOverlay(this.imergOverlay)
			// Reclaim GPU memory while hidden: purge the series (resets its
			// display state) plus any cached frames outside the series;
			// frames reload from the SQLite image cache when re-enabled
			this.imergOverlay.purge()
			for (const frame of this.frameCache.values()) {
				frame.purge()
			}
		}
	}

	override dispose(): void {
		// Unsubscribe from Redux state changes
		if (this.unsubscribe) {
			this.unsubscribe()
			this.unsubscribe = null
		}

		// Purge all cached frames
		for (const frame of this.frameCache.values()) {
			frame.purge()
		}
		this.frameCache.clear()

		this.earth.removeOverlay(this.imergOverlay)
		super.dispose()
	}
}
